import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Auth } from 'aws-amplify';
import { userAPI } from '../services/apiService';

// FMLA Doctor User interface - EXACT MATCH with backend util/models.go User struct
export interface User {
  email: string;                         // Primary key (matches Go json:"email")
  name: string;                          // User's full name (matches Go json:"name") 
  createdAt: string;                     // When account was created (matches Go json:"createdAt")
  lastLogin?: string;                    // Last login timestamp (matches Go json:"lastLogin,omitempty")
  userType: string;                      // "patient" | "provider" (matches Go json:"userType")
  // Patient-specific fields
  employmentState?: string;              // US state for provider matching (matches Go json:"employmentState,omitempty")
  serviceType?: string;                  // "fmla" | "fmla-std" | "follow-up" (matches Go json:"serviceType,omitempty")
  paymentAmount?: number;                // Amount paid for service - Go int becomes JS number (matches Go json:"paymentAmount,omitempty")
  // Provider-specific fields
  licensedStates?: string[];             // States where licensed (matches Go json:"licensedStates,omitempty")
  providerKey?: string;                  // Provider invitation key (matches Go json:"providerKey,omitempty")
  onboardingStatus?: string;             // Provider onboarding status (matches Go json:"onboardingStatus,omitempty")
  // Account status
  isActive?: boolean;                    // Account active status (matches Go json:"isActive,omitempty")
  // HIPAA retention
  ttl?: number;                          // 7-year retention (matches Go json:"ttl,omitempty") - Go int64 becomes JS number
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (user: User) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  refreshUserProfile: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(() => {
    // Initialize from localStorage if available
    try {
      const storedUser = localStorage.getItem('fmla_user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        return {
          isAuthenticated: true,
          isLoading: true, // Still loading to verify with Cognito
          user: userData,
          error: null,
        };
      }
    } catch (error) {
    }
    
    return {
      isAuthenticated: false,
      isLoading: true,
      user: null,
      error: null,
    };
  });

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  // Load user profile data for FMLA users (patient or provider)
  const loadUserProfile = async (baseUser: User) => {
    try {
      const profile = await userAPI.getProfile();
      
      const enhancedUser: User = {
        ...baseUser,
        userType: profile.userType || baseUser.userType,
        // Patient-specific fields
        employmentState: profile.employmentState || baseUser.employmentState,
        serviceType: profile.serviceType || baseUser.serviceType,
        paymentAmount: profile.paymentAmount || baseUser.paymentAmount,
        // Provider-specific fields (from backend if available)
        licensedStates: (profile as any).licensedStates || baseUser.licensedStates,
        providerKey: (profile as any).providerKey || baseUser.providerKey,
        onboardingStatus: (profile as any).onboardingStatus || baseUser.onboardingStatus,
      };
      
      // Update state with enhanced user data
      updateUser(enhancedUser);
    } catch (error) {
      // Don't fail authentication if profile loading fails
    }
  };

  // Helper function to determine if user data is more complete
  const isMoreCompleteUserData = (newUser: User, existingUser: User | null): boolean => {
    if (!existingUser) return true;
    
    // Don't overwrite complete names with fallback values
    if (existingUser.name && existingUser.name !== 'User' && 
        (!newUser.name || newUser.name === 'User')) {
      return false;
    }
    
    // Prefer data with more fields populated
    const existingFields = [existingUser.name, existingUser.email].filter(Boolean).length;
    const newFields = [newUser.name, newUser.email].filter(Boolean).length;
    
    return newFields >= existingFields;
  };

  const checkAuthState = async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const user = await Auth.currentAuthenticatedUser();
      if (user) {
        // For OAuth users, user attributes might be in the ID token payload instead
        const session = user.signInUserSession;
        const idToken = session?.getIdToken();
        const tokenPayload = idToken?.payload || {};
        
        // Try both user.attributes and token payload
        const userAttributes = user.attributes || {};
        const email = tokenPayload.email || userAttributes.email || userAttributes['cognito:username'] || '';
        const userId = tokenPayload.sub || userAttributes.sub || user.username || 'unknown-id';
        
        // Build full name from available sources
        let userName = '';
        if (tokenPayload.given_name && tokenPayload.family_name) {
          userName = `${tokenPayload.given_name} ${tokenPayload.family_name}`;
        } else if (tokenPayload.name) {
          userName = tokenPayload.name;
        } else if (userAttributes.given_name && userAttributes.family_name) {
          userName = `${userAttributes.given_name} ${userAttributes.family_name}`;
        } else if (userAttributes.name) {
          userName = userAttributes.name;
        } else if (tokenPayload.given_name) {
          userName = tokenPayload.given_name;
        } else if (userAttributes.given_name) {
          userName = userAttributes.given_name;
        } else if (email) {
          userName = email.split('@')[0];
        } else {
          userName = 'User';
        }
        
        const newUserData: User = {
          email: email,
          name: userName,
          createdAt: new Date().toISOString(), // Current time as fallback
          userType: 'patient', // Default to patient, will be updated by loadUserProfile
          employmentState: '', // Will be populated by loadUserProfile for patients
          serviceType: 'fmla', // Default, will be updated by loadUserProfile for patients
          paymentAmount: 100,   // Default, will be updated by loadUserProfile for patients
          // Provider fields will be populated by loadUserProfile if applicable
        };
        
        // Get existing user data from current state (which initialized from localStorage)
        const existingUser = authState.user;
        
        // Only update if new data is more complete than existing data
        if (isMoreCompleteUserData(newUserData, existingUser)) {
          // Merge with existing data to preserve complete fields
          const mergedUserData: User = {
            ...existingUser,
            ...newUserData,
            // Preserve existing complete name if new name is fallback
            name: (existingUser?.name && existingUser.name !== 'User' && newUserData.name === 'User') 
              ? existingUser.name 
              : newUserData.name,
          };
          
          // Store in localStorage for persistence
          localStorage.setItem('fmla_user', JSON.stringify(mergedUserData));
          
          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user: mergedUserData,
            error: null,
          });
          
          // Load full profile data with usage information in background
          loadUserProfile(mergedUserData);
          
          // Dispatch custom event for real-time synchronization
          window.dispatchEvent(new CustomEvent('userDataUpdated', {
            detail: { user: mergedUserData }
          }));
        } else {
          // Just update authentication status without overwriting user data
          setAuthState(prev => ({
            ...prev,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          }));
          
          // Still load profile data in background if we have existing user
          if (existingUser) {
            loadUserProfile(existingUser);
          }
        }
      } else {
        // Clear localStorage on logout
        localStorage.removeItem('fmla_user');
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: null,
        });
      }
    } catch (error) {
      // Clear localStorage on auth error
      localStorage.removeItem('fmla_user');
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
    }
  };

  const login = useCallback((user: User) => {
    // Store in localStorage
    localStorage.setItem('fmla_user', JSON.stringify(user));
    
    setAuthState({
      isAuthenticated: true,
      isLoading: false,
      user,
      error: null,
    });
    
    // Dispatch custom event for real-time synchronization
    window.dispatchEvent(new CustomEvent('userDataUpdated', {
      detail: { user }
    }));
  }, []);

  const logout = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem('fmla_user');
    
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null,
    });
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    setAuthState(prev => {
      const updatedUser = prev.user ? { ...prev.user, ...updates } : null;
      
      // Update localStorage with new user data
      if (updatedUser) {
        localStorage.setItem('fmla_user', JSON.stringify(updatedUser));
        
        // Dispatch custom event for real-time synchronization
        window.dispatchEvent(new CustomEvent('userDataUpdated', {
          detail: { user: updatedUser }
        }));
      }
      
      return {
        ...prev,
        user: updatedUser,
      };
    });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setAuthState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setAuthState(prev => ({ ...prev, error, isLoading: false }));
  }, []);

  const refreshUserProfile = useCallback(async () => {
    if (authState.user) {
      await loadUserProfile(authState.user);
    }
  }, [authState.user]);

  const contextValue: AuthContextType = {
    ...authState,
    login,
    logout,
    updateUser,
    refreshUserProfile,
    setLoading,
    setError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};